import axios from 'axios';
import { Reminder } from './reminderService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// Types
export interface DashboardData {
  userInfo: UserDashboardInfo;
  remindersSummary: RemindersSummary;
  systemAdminData?: SystemAdminDashboardData;
  // מובילי החודש (30 הימים האחרונים) — קיים לכל הצוות
  topPerformers?: TopPerformer[];
  // פילוח לפי מחלקות — למנהל בלבד (revenue מוצג רק אם רשאי לצפות בדוחות פיננסיים)
  departmentBreakdown?: DepartmentBreakdownItem[];
}

// מוביל בטבלת המצטיינים — ספירת יצירות (לידים/תיקים) ב-30 הימים האחרונים
export interface TopPerformer {
  userId: number;
  userName: string;
  departmentName?: string;
  clientsCreated: number;
  totalCreated: number;
}

// שורת פילוח מחלקתי — תצוגת סיווג בלבד (ללא השלכות נראוּת)
export interface DepartmentBreakdownItem {
  departmentId: number | null;
  departmentName: string;
  leadsCount: number;
  clientsCount: number;
  conversionRate: number; // אחוזים
  revenue: number | null; // null כאשר אין הרשאה לצפייה בנתונים פיננסיים
}

export interface UserDashboardInfo {
  id: number;
  fullName: string;
  email: string;
}

export interface RemindersSummary {
  todayCount: number;
  overdueCount: number;
  upcomingCount: number;
  todayReminders: Reminder[];
  overdueReminders: Reminder[];
  upcomingReminders: Reminder[];
}

export interface LeadStatusSummary {
  statusId: number;
  statusName: string;
  statusColor: string;
  count: number;
  teamCount: number; // For supervisors - team-wide count
}

export interface MonthlyPerformance {
  newLeadsReceived: number;
  signatures: number;
  conversionRate: number;
  teamRanking: number;
  totalTeamMembers: number;
}

export interface LeadsPipelineSummary {
  redStatusCount: number;
  orangeStatusCount: number;
  greenStatusCount: number;
  totalActiveLeads: number;
}

export interface TeamOverview {
  totalTeamLeads: number;
  unhandledLeadsCount: number;
  teamPipeline: LeadsPipelineSummary;
  teamSignaturesThisMonth: number;
  teamConversionRate: number;
  teamPerformance: MonthlyPerformance;
  teamNewLeadsCount: number;
  teamAwaitingDecisionCount: number;
}

export interface SystemAdminDashboardData {
  systemStats: SystemStats;
  recentUserActivity: UserActivitySummary[];
  pendingApprovalsCount: number;
  systemPerformance: MonthlyPerformance;
  systemPipeline: LeadsPipelineSummary;
  systemNewLeadsCount: number;
  systemAwaitingDecisionCount: number;
  systemSignedThisMonthCount: number;
  leadStatuses: LeadStatusSummary[]; // Dynamic status counts for admin

  // New enhanced metrics
  financialOverview: FinancialOverview;
  casesMetrics: CasesMetrics;
  cashFlowTrend: CashFlowTrend[];
  urgentTasksSummary: AlertsSummary;

  // Status distributions
  leadStatusDistribution: StatusDistribution[];
  clientStatusDistribution: StatusDistribution[];

  // Special Payments Summary
  specialPaymentsSummary: SpecialPaymentsSummary;
}

export interface SystemStats {
  totalUsers: number;
  activeUsers: number;
  totalLeads: number;
  totalClients: number;
  totalCases: number;
}

export interface UserActivitySummary {
  userId: number;
  userName: string;
  lastActivityType: string;
  lastActivityTime: string;
}

// Financial Overview
export interface FinancialOverview {
  actualIncomeThisMonth: number;
  actualIncomeLastMonth: number;
  actualIncomePercentageChange: number;
  expectedIncomeNextMonth: number;
  expectedIncomePercentageChange: number;
  openDebtsBalance: number;
  openDebtsPercentageChange: number;
  activePaymentPlansCount: number;
  overduePaymentPlansCount: number;
}

// Cases Metrics
export interface CasesMetrics {
  activeCasesCount: number;
  committeesThisMonth: number;
  committeesThisWeek: number;
  approvedCasesThisMonth: number;
  rejectedCasesThisMonth: number;
  totalMonthlyBenefitsAchieved: number;
  totalRetroactiveBenefitsAchieved: number;
}

// Cash Flow Trend
export interface CashFlowTrend {
  monthOffset: number;
  monthName: string;
  monthDate: string;
  actualAmount: number;
  forecastAmount: number;
  isForecast: boolean;
}

// Alerts Summary
export interface AlertsSummary {
  coldLeadsCount: number;
  overduePaymentsCount: number;
  upcomingCommitteesCount: number;
  pendingDocumentsCount: number;
}

// Status Distribution
export interface StatusDistribution {
  statusId: number;
  statusName: string;
  statusColor: string;
  count: number;
  percentage: number;
}

// API calls
const dashboardApi = {
  getDashboardData: async (): Promise<DashboardData> => {
    const response = await axios.get(`${API_BASE_URL}/dashboard`);
    return response.data;
  }
};

// Special Payments Summary
export interface SpecialPaymentsSummary {
  activePaymentsCount: number;
  activePaymentsAmount: number;
  paidThisMonthCount: number;
  paidThisMonthAmount: number;
  totalEverPaid: number;
  uniqueClientsCount: number;
}

// Helper functions
export const getPipelineStatusColor = (status: 'red' | 'orange' | 'green'): string => {
  switch (status) {
    case 'red': return 'var(--error-color)';
    case 'orange': return 'var(--warning-color)';
    case 'green': return 'var(--success-color)';
    default: return 'var(--text-secondary)';
  }
};

export const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

// טיפול אחיד בשגיאות API — מקור אמת מרכזי
export { handleApiError } from '../utils/errorHandler';

export default dashboardApi;